import { getJWTToken } from '../../utils/auth';
import { authHeaders } from '../../core/impersonation';
import { parseRosterMeta, type RosterMeta } from '../../core/rosterMeta';
import type { RowsPerPage } from '../../components/RowsPerPageSelect';
import { applyFilters, categoricalValues, type CategoricalDimension, type FilterChip } from './filters';
import { enumerateValues } from './dimensions';
import { suggestFilters, type FilterSuggestion } from './filterSuggestions';
import { departedEmptyState, scopeByEnrollment } from './enrollmentScope';
import { accessStudentCount } from './bannerState';
import {
  classificationToRosterRow,
  filterRows,
  filterRowsBySchool,
  pageRows,
  sortRowsByName,
  type PlanSignOff,
  type RosterRow,
} from './rosterRows';
import {
  followRosterHandoff,
  isClassificationRow,
  isPlanSignOff,
  parseRosterClassificationResult,
  type PlanSignOffsResult,
  type RosterClassificationResult,
  type RosterClassificationRow,
} from './rosterClassification';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export const TYPING_DEBOUNCE_MS = 200;

export type RosterQuery = {
  page: number;
  size: RowsPerPage;
  sort: 'asc' | 'desc';
  q: string;
  school: string;
  departed: boolean;
  filters: FilterChip[];
};

export type RosterCounts = {
  page: number;
  size: number;
  total: number;
  scoped: number;
  all: number;
  access: number;
  schools: number;
  allDeparted: boolean;
};

export type RosterFacetDimension = 'school' | 'grade' | 'teacher' | 'dataQuality';

export type RosterFacets = Record<RosterFacetDimension, string[]>;

export type RosterSignOffs = { year: string; generated: boolean; available: boolean; statuses: number };

export type RosterPageRow = RosterClassificationRow & { planSignOff?: PlanSignOff };

export type RosterPageResult = {
  students: RosterPageRow[];
  counts: RosterCounts;
  facets: RosterFacets;
  signOffs: RosterSignOffs | null;
  roster: RosterMeta | null;
  lastUploadedAt: string | null;
};

export type RosterPageAnswer = RosterPageResult | { legacy: RosterClassificationResult };

export type RosterSuggestions = { suggestions: FilterSuggestion[]; students: RosterRow[] };

export type RosterPageContext = { admin: boolean; signOffsKey: string; onSignOffs?: () => void };

export type MaybePromise<T> = T | Promise<T>;

export type RosterPageSource = {
  page(query: RosterQuery, context: RosterPageContext): MaybePromise<RosterPageAnswer | null>;
  suggest(q: string, query: RosterQuery, context: RosterPageContext): MaybePromise<RosterSuggestions | null>;
};

export const EMPTY_FACETS: RosterFacets = { school: [], grade: [], teacher: [], dataQuality: [] };

const FACET_DIMENSIONS: readonly RosterFacetDimension[] = ['school', 'grade', 'teacher', 'dataQuality'];

const COUNT_FIELDS = ['page', 'size', 'total', 'scoped', 'all', 'access', 'schools'] as const;

export type RosterPageBody = {
  page: number;
  size?: number;
  sort?: 'asc' | 'desc';
  q?: string;
  school?: string;
  departed?: boolean;
  filters?: FilterChip[];
  suggest?: string;
};

function scopeBody(query: RosterQuery): Partial<RosterPageBody> {
  const body: Partial<RosterPageBody> = {};
  if (query.school !== '') body.school = query.school;
  if (query.departed) body.departed = true;
  if (query.filters.length > 0) body.filters = query.filters;
  return body;
}

export function rosterPageBody(query: RosterQuery): RosterPageBody {
  const body: RosterPageBody = { page: query.page, size: query.size, sort: query.sort };
  if (query.q !== '') body.q = query.q;
  return { ...body, ...scopeBody(query) };
}

export function rosterSuggestBody(q: string, query: RosterQuery): RosterPageBody {
  return { page: 1, suggest: q, ...scopeBody(query) };
}

function isStringList(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string');
}

function isPageRow(value: unknown): value is RosterPageRow {
  if (!isClassificationRow(value)) return false;
  const planSignOff = (value as { planSignOff?: unknown }).planSignOff;
  return planSignOff === undefined || isPlanSignOff(planSignOff);
}

function parseCounts(value: unknown): RosterCounts | null {
  if (typeof value !== 'object' || value === null) return null;
  const counts = value as Record<string, unknown>;
  if (!COUNT_FIELDS.every((field) => typeof counts[field] === 'number')) return null;
  if (typeof counts.allDeparted !== 'boolean') return null;
  return {
    page: counts.page as number,
    size: counts.size as number,
    total: counts.total as number,
    scoped: counts.scoped as number,
    all: counts.all as number,
    access: counts.access as number,
    schools: counts.schools as number,
    allDeparted: counts.allDeparted,
  };
}

function parseFacets(value: unknown): RosterFacets | null {
  if (typeof value !== 'object' || value === null) return null;
  const facets = value as Record<string, unknown>;
  if (!FACET_DIMENSIONS.every((dimension) => isStringList(facets[dimension]))) return null;
  return {
    school: facets.school as string[],
    grade: facets.grade as string[],
    teacher: facets.teacher as string[],
    dataQuality: facets.dataQuality as string[],
  };
}

function parseSignOffs(value: unknown): RosterSignOffs | null | undefined {
  if (value === undefined || value === null) return null;
  if (typeof value !== 'object') return undefined;
  const signOffs = value as Record<string, unknown>;
  if (
    typeof signOffs.year !== 'string' ||
    typeof signOffs.generated !== 'boolean' ||
    typeof signOffs.available !== 'boolean' ||
    typeof signOffs.statuses !== 'number'
  ) {
    return undefined;
  }
  return { year: signOffs.year, generated: signOffs.generated, available: signOffs.available, statuses: signOffs.statuses };
}

export function parseRosterPage(body: unknown): RosterPageAnswer | null {
  if (typeof body !== 'object' || body === null) return null;
  const page = body as Record<string, unknown>;
  if (page.page === undefined) {
    const legacy = parseRosterClassificationResult(page);
    return legacy === null ? null : { legacy };
  }
  const students = page.students;
  if (!Array.isArray(students) || !students.every(isPageRow)) return null;
  const counts = parseCounts(page.page);
  const facets = parseFacets(page.facets);
  const signOffs = parseSignOffs(page.signOffs);
  if (counts === null || facets === null || signOffs === undefined) return null;
  return {
    students,
    counts,
    facets,
    signOffs,
    roster: parseRosterMeta(page.roster),
    lastUploadedAt: typeof page.lastUploadedAt === 'string' ? page.lastUploadedAt : null,
  };
}

function isSuggestion(value: unknown): value is FilterSuggestion {
  if (typeof value !== 'object' || value === null) return false;
  const suggestion = value as Record<string, unknown>;
  return (
    typeof suggestion.dimension === 'string' &&
    typeof suggestion.dimensionLabel === 'string' &&
    typeof suggestion.value === 'string' &&
    typeof suggestion.valueLabel === 'string'
  );
}

export function parseRosterSuggestions(body: unknown): RosterSuggestions | null {
  if (typeof body !== 'object' || body === null) return null;
  const answer = body as Record<string, unknown>;
  if (!Array.isArray(answer.suggestions) || !answer.suggestions.every(isSuggestion)) return null;
  if (!Array.isArray(answer.students) || !answer.students.every(isClassificationRow)) return null;
  return { suggestions: answer.suggestions, students: answer.students.map(classificationToRosterRow) };
}

async function postRoster(body: RosterPageBody): Promise<unknown> {
  const token = await getJWTToken();
  const res = await fetch(`${apiUrl}/roster/classification`, {
    method: 'POST',
    headers: { ...(token ? authHeaders(token) : {}), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) return null;
  return res.json();
}

export async function fetchRosterPage(query: RosterQuery): Promise<RosterPageAnswer | null> {
  try {
    const body = await postRoster(rosterPageBody(query));
    if (body === null) return null;
    return parseRosterPage(await followRosterHandoff(body as Parameters<typeof followRosterHandoff>[0]));
  } catch {
    return null;
  }
}

export async function fetchRosterSuggestions(q: string, query: RosterQuery): Promise<RosterSuggestions | null> {
  try {
    const body = await postRoster(rosterSuggestBody(q, query));
    return body === null ? null : parseRosterSuggestions(body);
  } catch {
    return null;
  }
}

function coalesced<T>(delayMs: number): (run: () => Promise<T | null>) => Promise<T | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let supersede: (() => void) | undefined;
  return (run) =>
    new Promise((resolve) => {
      if (timer !== undefined) clearTimeout(timer);
      supersede?.();
      supersede = () => resolve(null);
      timer = setTimeout(() => {
        timer = undefined;
        supersede = undefined;
        run().then(resolve, () => resolve(null));
      }, delayMs);
    });
}

export function serverRosterSource(): RosterPageSource {
  const typing = coalesced<RosterPageAnswer>(TYPING_DEBOUNCE_MS);
  const suggesting = coalesced<RosterSuggestions>(TYPING_DEBOUNCE_MS);
  let lastQ: string | undefined;
  return {
    page(query) {
      const typed = lastQ !== undefined && query.q !== lastQ;
      lastQ = query.q;
      return typed ? typing(() => fetchRosterPage(query)) : fetchRosterPage(query);
    },
    suggest(q, query) {
      return suggesting(() => fetchRosterSuggestions(q, query));
    },
  };
}

export const serverRosterPager: RosterPageSource = serverRosterSource();

type SignOffSource = () => Promise<PlanSignOffsResult | null>;

type SignOffsAsked = { key: string; result: PlanSignOffsResult | null | undefined };

function hasSignOffChip(chips: FilterChip[]): boolean {
  return chips.some((chip) => chip.dimension === 'planSignoff');
}

function withoutSignOffChip(chips: FilterChip[]): FilterChip[] {
  return chips.filter((chip) => chip.dimension !== 'planSignoff');
}

function facetsOf(rows: RosterRow[]): RosterFacets {
  return {
    school: enumerateValues('school', rows).map((option) => option.value),
    grade: enumerateValues('grade', rows).map((option) => option.value),
    teacher: enumerateValues('teacher', rows).map((option) => option.value),
    dataQuality: enumerateValues('dataQuality', rows).map((option) => option.value),
  };
}

export function localRosterPager(result: RosterClassificationResult, signOffSource?: SignOffSource): RosterPageSource {
  const classifications = new WeakMap<RosterRow, RosterClassificationRow>();
  const rows = result.students.map((classification) => {
    const row = classificationToRosterRow(classification);
    classifications.set(row, classification);
    return row;
  });
  let asked: SignOffsAsked | undefined;

  const scope = (query: RosterQuery, context: RosterPageContext) => {
    const schoolRows = filterRowsBySchool(rows, query.school);
    const wanted = hasSignOffChip(query.filters) && context.admin;
    if (!wanted || signOffSource === undefined) {
      return { schoolRows, chips: withoutSignOffChip(query.filters), signOffs: null, statuses: null as PlanSignOffsResult | null };
    }
    if (asked === undefined || asked.key !== context.signOffsKey) {
      const current: SignOffsAsked = { key: context.signOffsKey, result: undefined };
      asked = current;
      signOffSource()
        .catch(() => null)
        .then((answer) => {
          if (asked !== current) return;
          current.result = answer;
          context.onSignOffs?.();
        });
    }
    const statuses = asked.result;
    if (statuses === undefined) {
      return { schoolRows, chips: withoutSignOffChip(query.filters), signOffs: null, statuses: null };
    }
    if (statuses === null) {
      return {
        schoolRows,
        chips: withoutSignOffChip(query.filters),
        signOffs: { year: '', generated: false, available: false, statuses: 0 },
        statuses: null,
      };
    }
    const signed = schoolRows.map((row) => {
      const joined = { ...row, planSignOff: statuses.students[row.id] ?? null };
      classifications.set(joined, classifications.get(row) as RosterClassificationRow);
      return joined;
    });
    return {
      schoolRows: signed,
      chips: query.filters,
      signOffs: { year: statuses.year, generated: statuses.generated, available: true, statuses: Object.keys(statuses.students).length },
      statuses,
    };
  };

  const toPageRow = (row: RosterRow): RosterPageRow => {
    const classification = classifications.get(row) as RosterClassificationRow;
    return row.planSignOff == null ? classification : { ...classification, planSignOff: row.planSignOff };
  };

  return {
    page(query, context) {
      const { schoolRows, chips, signOffs } = scope(query, context);
      const scoped = scopeByEnrollment(schoolRows, query.departed);
      const matched = sortRowsByName(applyFilters(filterRows(scoped, query.q), chips), query.sort);
      return {
        students: pageRows(matched, query.page, query.size).map(toPageRow),
        counts: {
          page: query.page,
          size: query.size,
          total: matched.length,
          scoped: scoped.length,
          all: rows.length,
          access: accessStudentCount(rows),
          schools: new Set(rows.map((row) => row.school.trim().toLowerCase())).size,
          allDeparted: departedEmptyState(schoolRows, query.departed),
        },
        facets: facetsOf(schoolRows),
        signOffs,
        roster: result.roster,
        lastUploadedAt: result.lastUploadedAt,
      };
    },
    suggest(q, query, context) {
      if (q.trim() === '') return { suggestions: [], students: [] };
      const { schoolRows, chips } = scope(query, context);
      return {
        suggestions: suggestFilters(q, schoolRows, chips, undefined, context.admin),
        students: sortRowsByName(filterRows(applyFilters(schoolRows, chips), q), 'asc').slice(0, 3),
      };
    },
  };
}

export function deferredRosterPager(
  load: () => Promise<RosterClassificationRow[] | RosterClassificationResult | null>,
  signOffSource?: SignOffSource,
): RosterPageSource {
  let inner: RosterPageSource | null | undefined;
  let loading: Promise<RosterPageSource | null> | undefined;
  const ready = (): MaybePromise<RosterPageSource | null> => {
    if (inner !== undefined) return inner;
    loading ??= load()
      .catch(() => null)
      .then((answer) => {
        const classified = answer === null ? null : Array.isArray(answer) ? { students: answer, roster: null, lastUploadedAt: null } : answer;
        inner = classified === null ? null : localRosterPager(classified, signOffSource);
        return inner;
      });
    return loading;
  };
  return {
    page(query, context) {
      const source = ready();
      if (source instanceof Promise) return source.then((loaded) => (loaded === null ? null : loaded.page(query, context)));
      return source === null ? null : source.page(query, context);
    },
    suggest(q, query, context) {
      const source = ready();
      if (source instanceof Promise) return source.then((loaded) => (loaded === null ? null : loaded.suggest(q, query, context)));
      return source === null ? null : source.suggest(q, query, context);
    },
  };
}

export function categoricalFacetValues(dimension: CategoricalDimension, rows: RosterRow[]): string[] {
  return [...new Set(rows.flatMap((row) => categoricalValues(row, dimension)))];
}
